/**
 * Working together on one project: who is on what, right now, so two people's agent teams do
 * not work the same plan, roadmap row or risk without knowing
 * (docs/plans/2026-10-organisation-projects-and-working-together.md, part B). AIOE's own
 * contract; the workbench copies it into `shared/schemas/working.ts`.
 *
 * A **work claim** is a person's (and their team's) hold on a plan, a roadmap row or a risk,
 * taken before a team starts on it and released when it stops, as the coordinator's task claim
 * (`metadata.claimedBy`, coordinator.ts) is for a task. Tasks keep that claim; this adds the rest.
 *
 *   POST   /projects/v1/:id/claims                      contributors: { kind, ref, deviceId?, sessionId?, teamId?, note? }
 *                                                        → 201 the claim · 200 the claim with this session joined, when the same
 *                                                        person already holds it · 409 { error: "held", claim } when someone else does
 *   POST   /projects/v1/:id/claims/:claimId/heartbeat   the holder: still on it → the claim. Without one for
 *                                                        WORK_CLAIM_TTL_MINUTES the platform lets the claim go.
 *   DELETE /projects/v1/:id/claims/:claimId             the holder releases it; an owner takes it over (the holder is told) → 204
 *   GET    /projects/v1/:id/working                     viewers: { at, items[], people{} } — every held task claim, every
 *                                                        work claim and every run still going, each with who, what, where, since when
 *
 * A claim on a risk (`kind: "risk"`, any entry of the project's register: risk, issue or bug)
 * names it by its number (`RK-4`) or its uid in `ref.id`; the platform checks it is the project's
 * (404 `not_found` otherwise) and keeps the claim as `{ rootId: <the entry's repository, "" for
 * the project's>, id: <its number> }`, so a claim by uid and one by number are the same claim.
 * The working view gives a risk item the entry's title (AIOE 0.21.0).
 *
 * Working alongside (AIOE 0.35.0; the workbench's D247, C-8). `POST /projects/v1/:id/claims` with `{ kind: "roadmap", ref, alongside:
 * { planId, rootId?, by? } }` is the override: a plan is worked on a row another team's plan holds, by a person's choice. It never 409s
 * `held`: it records the choice on the row's claim (`alongside[]`, a new claim for the caller when the row has none), on both plans'
 * records (`rowAlongside[]`, plans.ts) and audits `plan.row.alongside`. The console reads "A-Team and Release train are both on R-4,
 * agreed by JP" (`alongsideWords`). Contributors and above.
 *
 * A claim on a roadmap row or a risk that someone else last changed or owns files a for-you
 * record to that person ("<name> started on R-4"). Words for people: working on, held by,
 * since, join, take over, release.
 */
import { z } from "zod";
export const WORKING_SCHEMA_VERSION = 1;
/** A claim with no heartbeat for this long is let go. */
export const WORK_CLAIM_TTL_MINUTES = 30;
export const WORK_CLAIM_KINDS = ["plan", "roadmap", "risk"];
export const workClaimKindSchema = z.enum(WORK_CLAIM_KINDS);
const idSchema = z.string().min(1).max(200);
/** What is claimed: a plan's id in a repository, a roadmap row (a repository's, or the project's with `rootId: ""`), a risk's id or uid. */
export const workClaimRefSchema = z.object({ rootId: z.string().max(200).default(""), id: idSchema });
export const workClaimRequestSchema = z.object({
    kind: workClaimKindSchema,
    ref: workClaimRefSchema,
    deviceId: z.string().min(1).max(100).optional(),
    sessionId: z.string().min(1).max(200).optional(),
    /** The team about to work it; `solo` for a specialist or a chat. */
    teamId: z.string().min(1).max(200).optional(),
    /** What the person means to do, for the others ("re-planning the migration"). */
    note: z.string().max(500).optional(),
    /** A plan worked alongside the holder's on this row, by the person's choice (AIOE 0.35.0, D247, C-8); `kind` is `roadmap`. */
    alongside: z.object({ planId: idSchema, /** The plan's repository; default the row's. */ rootId: z.string().max(200).optional(), /** The person who chose, by name; default the caller. */ by: z.string().min(1).max(200).optional() }).optional(),
});
/** One session of the holder's that is on the item; a second workbench or team of theirs joins as another. */
export const workClaimSessionSchema = z.object({
    deviceId: z.string().max(100).optional(),
    sessionId: z.string().max(200).optional(),
    teamId: z.string().max(200).optional(),
    at: z.string().max(50),
});
/** A plan worked alongside a row's holder (AIOE 0.35.0, D247, C-8), with the team's name and who agreed. */
export const workClaimAlongsideSchema = z.object({
    planId: idSchema,
    teamName: z.string().max(200),
    /** The person who chose, by name. */
    by: z.string().max(200),
    at: z.string().max(50),
    /** The plan it is alongside, when one held the row. */
    with: z.object({ planId: idSchema, teamName: z.string().max(200) }).optional(),
});
/** "A-Team and Release train are both on R-4, agreed by JP." */
export const alongsideWords = (rowId, a) => `${a.with?.teamName ?? "Another team"} and ${a.teamName} are both on ${rowId}, agreed by ${a.by}.`;
export const workClaimSchema = z.object({
    id: idSchema,
    kind: workClaimKindSchema,
    ref: workClaimRefSchema,
    /** The person who holds it. */
    principal: z.string().max(200),
    /** The agent that took it for them, when one did. */
    actor: z.string().max(200).optional(),
    note: z.string().max(500).optional(),
    /** When it was taken. */
    at: z.string().max(50),
    /** The last heartbeat (the taking counts as one). */
    heartbeatAt: z.string().max(50),
    sessions: z.array(workClaimSessionSchema).max(50),
    /** Plans worked on this row alongside the holder's, by a person's choice (AIOE 0.35.0, D247, C-8). */
    alongside: z.array(workClaimAlongsideSchema).max(20).optional(),
});
/** The document the platform keeps the project's claims in. */
export const workClaimsDocSchema = z.object({ schemaVersion: z.literal(WORKING_SCHEMA_VERSION).default(WORKING_SCHEMA_VERSION), claims: z.array(workClaimSchema).max(2000) });
/** Whether a claim has gone quiet: no heartbeat for the TTL. */
export function workClaimExpired(claim, nowMs) {
    const t = Date.parse(claim.heartbeatAt);
    return Number.isNaN(t) || nowMs - t > WORK_CLAIM_TTL_MINUTES * 60_000;
}
export const WORKING_ITEM_KINDS = ["task", "plan", "roadmap", "risk", "run"];
/** Words for a kind, for people. */
export const WORKING_KIND_TEXT = { task: "task", plan: "plan", roadmap: "roadmap item", risk: "risk", run: "run" };
//# sourceMappingURL=working.js.map